import { useState } from "react";
import { useTranslation } from "react-i18next";
import FileCategorySelect from "components/file-category-select/file-category-select";
import {
  resetWorkSecurityAreaSettings,
  updateWorkSecuritySettings,
} from "services/work-security.service";
import { WorkSecuritySettings } from "types/WorkSecuritySettings";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  StatusBanner,
  useInlineEdit,
  useSaveWithToast,
} from "../../ui";
import {
  TemplateSlotFields,
  TemplateSlotValue,
} from "../../template-slot-card";
import { useCategoryPaths } from "../../use-category-paths";
import ResetAreaSettingsRow from "./ResetAreaSettingsRow";
import {
  WorkSecurityRequest,
  incidentActSlotOf,
  incidentActSlotRequest,
  isSlotComplete,
  roleSlotOf,
  roleSlotRequest,
  uploadFoldersOf,
} from "./work-security-slots";

interface TemplateSlotRowProps {
  label: string;
  description: string;
  saved: TemplateSlotValue;
  /** The area still uses the company's values, so they cannot be changed here. */
  locked: boolean;
  categoryPath: (categoryId: string) => string;
  onSave: (slot: TemplateSlotValue) => Promise<unknown>;
}

function TemplateSlotRow({
  label,
  description,
  saved,
  locked,
  categoryPath,
  onSave,
}: TemplateSlotRowProps) {
  const { t } = useTranslation();
  const edit = useInlineEdit(saved);
  const { save, saving, error } = useSaveWithToast();
  const draft = edit.draft;
  // Changing the source also clears the template, so each change builds on the latest draft.
  const change = (changes: Partial<TemplateSlotValue>) =>
    edit.setDraft((current) => ({ ...current, ...changes }));

  const submit = async () => {
    if (await save(() => onSave(draft))) edit.cancel();
  };

  const look =
    saved.source === "custom"
      ? t("work_security_template_custom")
      : t("work_security_template_system");
  const where = saved.categoryId
    ? t("work_security_filed_in", { path: categoryPath(saved.categoryId) })
    : t("work_security_filed_nowhere");

  return (
    <SettingRow
      label={label}
      description={description}
      value={
        <>
          <div>
            {look} · {where}
          </div>
          {!isSlotComplete(saved) && (
            <div className="text-amber-800">
              {saved.source === "custom"
                ? t("select_template")
                : t("series_file_type_category_required")}
            </div>
          )}
        </>
      }
      action={locked ? undefined : <ChangeLink onClick={edit.start} />}
    >
      {edit.editing && (
        <EditPanel
          onSave={submit}
          onCancel={edit.cancel}
          saving={saving}
          error={error}
        >
          <TemplateSlotFields
            source={draft.source}
            templateId={draft.templateId}
            seriesId={draft.seriesId}
            fileTypeId={draft.fileTypeId}
            categoryId={draft.categoryId}
            configured={isSlotComplete(draft)}
            onSourceChange={(source) => change({ source })}
            onTemplateChange={(templateId) => change({ templateId })}
            onSeriesChange={(seriesId) => change({ seriesId })}
            onFileTypeChange={(fileTypeId) => change({ fileTypeId })}
            onCategoryChange={(categoryId) => change({ categoryId })}
          />
        </EditPanel>
      )}
    </SettingRow>
  );
}

interface UploadFoldersRowProps {
  saved: string[];
  locked: boolean;
  categoryPath: (categoryId: string) => string;
  onSave: (folderIds: string[]) => Promise<unknown>;
}

function UploadFoldersRow({
  saved,
  locked,
  categoryPath,
  onSave,
}: UploadFoldersRowProps) {
  const { t } = useTranslation();
  const edit = useInlineEdit(saved);
  const { save, saving, error } = useSaveWithToast();

  const submit = async () => {
    if (await save(() => onSave(edit.draft))) edit.cancel();
  };

  return (
    <SettingRow
      label={t("work_security_upload_folders")}
      description={t("work_security_upload_folders_hint")}
      value={
        saved.length > 0
          ? saved.map(categoryPath).join(", ")
          : t("work_security_upload_folders_none")
      }
      action={locked ? undefined : <ChangeLink onClick={edit.start} />}
    >
      {edit.editing && (
        <EditPanel
          onSave={submit}
          onCancel={edit.cancel}
          saving={saving}
          error={error}
        >
          <FileCategorySelect
            value={edit.draft}
            onChange={(folderIds: string[]) => edit.setDraft(folderIds || [])}
            isRequired={false}
            subject="customer"
            allowMultiple
          />
        </EditPanel>
      )}
    </SettingRow>
  );
}

interface DocumentsCardProps {
  companyId: string;
  /** Empty: the company's own documents. Otherwise the area whose documents these are. */
  areaId: string;
  /** The company record as seen for that scope. */
  entity: WorkSecuritySettings;
  /** The incident act exists for the company as a whole and for areas that register incidents. */
  showIncidentAct: boolean;
  /** Reloads `entity` after a save. */
  onSaved: () => Promise<unknown>;
}

/** The documents the system makes for procedures, and the folders existing documents may be attached from. */
export default function DocumentsCard({
  companyId,
  areaId,
  entity,
  showIncidentAct,
  onSaved,
}: DocumentsCardProps) {
  const { t } = useTranslation();
  const categoryPath = useCategoryPaths(companyId);
  const [ownSettings, setOwnSettings] = useState(false);
  const [resetting, setResetting] = useState(false);

  // An area starts on the company's values; nothing can be edited until it asks for its own.
  // Locked rows send nothing: saving them with an areaId would copy the company's values
  // into a row of the area, which would then stop following the company.
  // While the area's own settings are being deleted its rows stay locked too: a
  // save in that window would bring the deleted row back.
  const inherits = (configSource: string | undefined) =>
    Boolean(areaId) &&
    (resetting || (configSource === "company" && !ownSettings));
  const roleLocked = inherits(entity.workSecurityRoleConfigSource);
  const incidentActLocked =
    showIncidentAct && inherits(entity.workSecurityIncidentActConfigSource);

  const hasOwnSettings =
    Boolean(areaId) &&
    (entity.workSecurityRoleConfigSource === "area" ||
      entity.workSecurityIncidentActConfigSource === "area");

  const resetToCompanySettings = async () => {
    setResetting(true);
    try {
      await resetWorkSecurityAreaSettings(companyId, areaId);
      // The area is on the company's settings again, so the rows lock until it asks for its own.
      setOwnSettings(false);
      await onSaved();
    } finally {
      setResetting(false);
    }
  };

  const saveRequest = async (request: WorkSecurityRequest) => {
    await updateWorkSecuritySettings(companyId, {
      ...request,
      ...(areaId ? { areaId } : {}),
    });
    await onSaved();
  };

  return (
    <div className="space-y-5">
      {(roleLocked || incidentActLocked) && (
        <StatusBanner
          tone="info"
          title={t("work_security_settings_inherited_banner_title")}
          detail={t("work_security_settings_inherited_banner_desc")}
          action={
            <button
              type="button"
              onClick={() => setOwnSettings(true)}
              className="inline-flex min-h-11 items-center md:min-h-9 [@media(pointer:coarse)]:min-h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-900 hover:bg-gray-50"
            >
              {t("work_security_settings_use_own")}
            </button>
          }
        />
      )}

      <SettingsCard
        title={t("work_security_documents_title")}
        description={t(
          areaId
            ? "work_security_documents_hint_area"
            : "work_security_documents_hint"
        )}
      >
        <TemplateSlotRow
          label={t("work_security_role_document")}
          description={t("work_security_role_document_hint")}
          saved={roleSlotOf(entity)}
          locked={roleLocked}
          categoryPath={categoryPath}
          onSave={(slot) => saveRequest(roleSlotRequest(slot))}
        />
        {showIncidentAct && (
          <TemplateSlotRow
            label={t("work_security_incident_act")}
            description={t("work_security_incident_act_hint")}
            saved={incidentActSlotOf(entity)}
            locked={incidentActLocked}
            categoryPath={categoryPath}
            onSave={(slot) => saveRequest(incidentActSlotRequest(slot))}
          />
        )}
        {/* The folders are stored with the role document's settings, so they follow its inheritance. */}
        <UploadFoldersRow
          saved={uploadFoldersOf(entity)}
          locked={roleLocked}
          categoryPath={categoryPath}
          onSave={(folderIds) =>
            saveRequest({ workSecurityRoleUploadCategories: folderIds })
          }
        />
        {hasOwnSettings && (
          <ResetAreaSettingsRow onReset={resetToCompanySettings} />
        )}
      </SettingsCard>
    </div>
  );
}
